import type { RepositoryFile } from '@trove/core';

const IMAGE_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'avif',
  'heic',
  'heif',
]);
const HTML_EXTENSIONS = new Set(['html', 'htm']);
const CODE_EXTENSIONS = new Set([
  'js',
  'jsx',
  'ts',
  'tsx',
  'css',
  'scss',
  'yml',
  'yaml',
  'toml',
  'xml',
  'sh',
]);
const DELIMITED_SHEET_EXTENSIONS = new Set(['csv', 'tsv']);
const WORKBOOK_EXTENSIONS = new Set(['xls', 'xlsx']);
const SHEET_EXTENSIONS = new Set([...DELIMITED_SHEET_EXTENSIONS, ...WORKBOOK_EXTENSIONS]);
const AUDIO_EXTENSIONS = new Set(['mp3', 'm4a', 'aac', 'wav', 'ogg', 'flac', 'oga']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'm4v', 'ogv']);
const DOCUMENT_EXTENSIONS = new Set(['docx']);
const ARCHIVE_EXTENSIONS = new Set(['zip']);
const CONTACT_EXTENSIONS = new Set(['vcf', 'vcard']);
const CALENDAR_EXTENSIONS = new Set(['ics', 'ical']);
const TEXT_EXTENSIONS = new Set([
  'md',
  'markdown',
  'mdx',
  'txt',
  'json',
  'csv',
  'tsv',
  'xml',
  'yml',
  'yaml',
  'toml',
  'js',
  'jsx',
  'ts',
  'tsx',
  'css',
  'scss',
  'html',
  'htm',
  'sh',
  'vcf',
  'vcard',
  'ics',
  'ical',
]);

export type FileVisualKind =
  | 'image'
  | 'html'
  | 'pdf'
  | 'json'
  | 'sheet'
  | 'markdown'
  | 'text'
  | 'code'
  | 'audio'
  | 'video'
  | 'document'
  | 'archive'
  | 'contact'
  | 'calendar'
  | 'binary';

export function extensionOf(path: string) {
  const base = path.split('/').at(-1) ?? path;
  const dot = base.lastIndexOf('.');
  return dot >= 0 ? base.slice(dot + 1).toLowerCase() : '';
}

export function isMarkdownFile(path: string, mimeType: string): boolean {
  return (
    mimeType === 'text/markdown' ||
    mimeType === 'text/x-markdown' ||
    /\.(md|markdown|mdx)$/i.test(path)
  );
}

export function isImageFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  return file.mimeType.startsWith('image/') || IMAGE_EXTENSIONS.has(extensionOf(file.path));
}

/** HEIC/HEIF needs decode-to-JPEG before most browsers can paint a preview. */
export function isHeicFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  const ext = extensionOf(file.path);
  return (
    ext === 'heic' ||
    ext === 'heif' ||
    file.mimeType === 'image/heic' ||
    file.mimeType === 'image/heif'
  );
}

/** Prefer the browser File type; fall back from extension when Chrome leaves it empty. */
export function mimeTypeForUpload(path: string, fileType = '') {
  if (fileType) return fileType;
  const ext = extensionOf(path);
  if (ext === 'heic' || ext === 'heif') return 'image/heic';
  if (ext === 'svg') return 'image/svg+xml';
  if (ext === 'jpg') return 'image/jpeg';
  if (ext === 'xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (ext === 'xls') return 'application/vnd.ms-excel';
  if (ext === 'docx')
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (ext === 'mp3') return 'audio/mpeg';
  if (ext === 'm4a' || ext === 'aac') return 'audio/mp4';
  if (ext === 'wav') return 'audio/wav';
  if (ext === 'ogg' || ext === 'oga') return 'audio/ogg';
  if (ext === 'flac') return 'audio/flac';
  if (ext === 'mp4' || ext === 'm4v') return 'video/mp4';
  if (ext === 'webm') return 'video/webm';
  if (ext === 'mov') return 'video/quicktime';
  if (ext === 'ogv') return 'video/ogg';
  if (ext === 'zip') return 'application/zip';
  if (ext === 'vcf' || ext === 'vcard') return 'text/vcard';
  if (ext === 'ics' || ext === 'ical') return 'text/calendar';
  if (IMAGE_EXTENSIONS.has(ext)) return `image/${ext}`;
  if (AUDIO_EXTENSIONS.has(ext)) return `audio/${ext}`;
  if (VIDEO_EXTENSIONS.has(ext)) return `video/${ext}`;
  return 'application/octet-stream';
}

export function repositoryContentBlob(file: Pick<RepositoryFile, 'content' | 'mimeType'>) {
  if (file.content === undefined) throw new Error('File content is not loaded');
  if (!file.content.startsWith('data:')) {
    return new Blob([file.content], { type: file.mimeType || 'application/octet-stream' });
  }
  const comma = file.content.indexOf(',');
  if (comma < 0) {
    return new Blob([], { type: file.mimeType || 'application/octet-stream' });
  }
  const header = file.content.slice(5, comma);
  const payload = file.content.slice(comma + 1);
  const mime = header.split(';')[0] || file.mimeType || 'application/octet-stream';
  if (header.includes(';base64')) {
    const binary = atob(payload);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new Blob([bytes], { type: mime });
  }
  return new Blob([decodeURIComponent(payload)], { type: mime });
}

export function isHtmlFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  return file.mimeType === 'text/html' || HTML_EXTENSIONS.has(extensionOf(file.path));
}

export function isPdfFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  return file.mimeType === 'application/pdf' || extensionOf(file.path) === 'pdf';
}

export function isJsonFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  return file.mimeType === 'application/json' || extensionOf(file.path) === 'json';
}

export function isDelimitedSheetFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  const ext = extensionOf(file.path);
  return (
    file.mimeType === 'text/csv' ||
    file.mimeType === 'text/tab-separated-values' ||
    DELIMITED_SHEET_EXTENSIONS.has(ext)
  );
}

export function isWorkbookSheetFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  const ext = extensionOf(file.path);
  return (
    WORKBOOK_EXTENSIONS.has(ext) ||
    file.mimeType.includes('spreadsheetml') ||
    file.mimeType === 'application/vnd.ms-excel'
  );
}

export function isSheetFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  const ext = extensionOf(file.path);
  return (
    file.mimeType === 'text/csv' ||
    file.mimeType.includes('spreadsheet') ||
    SHEET_EXTENSIONS.has(ext)
  );
}

export function isAudioFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  return file.mimeType.startsWith('audio/') || AUDIO_EXTENSIONS.has(extensionOf(file.path));
}

export function isVideoFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  return file.mimeType.startsWith('video/') || VIDEO_EXTENSIONS.has(extensionOf(file.path));
}

export function isDocumentFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  return (
    DOCUMENT_EXTENSIONS.has(extensionOf(file.path)) ||
    file.mimeType.includes('wordprocessingml') ||
    file.mimeType === 'application/msword'
  );
}

export function isArchiveFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  return (
    ARCHIVE_EXTENSIONS.has(extensionOf(file.path)) ||
    file.mimeType === 'application/zip' ||
    file.mimeType === 'application/x-zip-compressed'
  );
}

export function isContactFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  return (
    CONTACT_EXTENSIONS.has(extensionOf(file.path)) ||
    file.mimeType === 'text/vcard' ||
    file.mimeType === 'text/x-vcard'
  );
}

export function isCalendarFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  return (
    CALENDAR_EXTENSIONS.has(extensionOf(file.path)) ||
    file.mimeType === 'text/calendar' ||
    file.mimeType === 'application/ics'
  );
}

export function isCodeFile(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return false;
  return CODE_EXTENSIONS.has(extensionOf(file.path));
}

export function fileVisualKind(file: Pick<RepositoryFile, 'path' | 'mimeType'>): FileVisualKind {
  if (isImageFile(file)) return 'image';
  if (isHtmlFile(file)) return 'html';
  if (isPdfFile(file)) return 'pdf';
  if (isJsonFile(file)) return 'json';
  if (isSheetFile(file)) return 'sheet';
  if (isDocumentFile(file)) return 'document';
  if (isAudioFile(file)) return 'audio';
  if (isVideoFile(file)) return 'video';
  if (isArchiveFile(file)) return 'archive';
  if (isContactFile(file)) return 'contact';
  if (isCalendarFile(file)) return 'calendar';
  if (isMarkdownFile(file.path, file.mimeType)) return 'markdown';
  if (isCodeFile(file)) return 'code';
  if (file.mimeType.startsWith('text/') || extensionOf(file.path) === 'txt') return 'text';
  return 'binary';
}

/** True when history should show a line diff instead of a media/binary preview. */
export function isLineDiffable(file?: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  if (!file) return true;
  const kind = fileVisualKind(file);
  if (kind === 'sheet') return isDelimitedSheetFile(file);
  return (
    kind === 'json' ||
    kind === 'markdown' ||
    kind === 'text' ||
    kind === 'code' ||
    kind === 'contact' ||
    kind === 'calendar'
  );
}

export function isTextEditable(file: Pick<RepositoryFile, 'path' | 'mimeType'>) {
  const kind = fileVisualKind(file);
  if (kind === 'sheet') return isDelimitedSheetFile(file);
  return (
    kind === 'json' ||
    kind === 'markdown' ||
    kind === 'text' ||
    kind === 'code' ||
    kind === 'html' ||
    kind === 'contact' ||
    kind === 'calendar'
  );
}

/** Browser File / vault path pairs that should be read as text on upload. */
export function isTextContentPath(path: string, mimeType = '') {
  if (mimeType.startsWith('text/') || mimeType === 'application/json') return true;
  return TEXT_EXTENSIONS.has(extensionOf(path));
}

export function downloadRepositoryFile(
  file: Pick<RepositoryFile, 'path' | 'content' | 'mimeType'>,
) {
  if (file.content === undefined) throw new Error('File content is not loaded');
  const temporary = !file.content.startsWith('data:');
  const url = temporary
    ? URL.createObjectURL(new Blob([file.content], { type: file.mimeType }))
    : file.content;
  const link = document.createElement('a');
  link.href = url;
  link.download = file.path.split('/').at(-1) ?? 'download';
  link.click();
  if (temporary) URL.revokeObjectURL(url);
}

export function fileObjectUrl(
  file: Pick<RepositoryFile, 'content' | 'mimeType'>,
  fallbackType = 'application/octet-stream',
) {
  if (file.content === undefined) throw new Error('File content is not loaded');
  if (file.content.startsWith('data:')) return file.content;
  return URL.createObjectURL(new Blob([file.content], { type: file.mimeType || fallbackType }));
}
